import React from "react";
import { NavLink } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Tooltip, Spinner } from "../../shared";
import { useGetDelegationsQuery, baseApi } from "../../shared";
import {
  HomeIcon,
  NewspaperIcon,
  CalendarIcon,
  ScaleIcon,
  ClipboardDocumentListIcon,
  FolderIcon,
  BuildingOfficeIcon,
  UsersIcon,
  StarIcon,
  UserIcon,
  HeartIcon,
  ChartBarIcon,
  MegaphoneIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  PresentationChartLineIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  CheckCircleIcon,
  MapIcon,
  GlobeAltIcon,
  ShoppingBagIcon,
  LightBulbIcon,
  HandRaisedIcon,
  DocumentCheckIcon,
} from "@heroicons/react/24/outline";
import {
  selectCurrentUser,
  selectDelegations,
  selectActiveDelegationTenantId,
  setDelegationContext,
  clearDelegationContext,
  selectHasPermission,
} from "../../store/slices/authSlice";
import { PERMISSIONS } from "../../shared";
import { useAllFeatureToggles } from "../../hooks/useFeatureToggle";

const Sidebar = ({ expanded, mobileOpen, onToggle, onMobileClose }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const delegations = useSelector(selectDelegations);
  const activeDelegationTenantId = useSelector(selectActiveDelegationTenantId);
  const {
    features,
    isLoading: featuresLoading,
    canViewDisabled,
  } = useAllFeatureToggles();

  // Load delegations — RTK Query caches and deduplicates; use live data as source of truth
  const { data: delegationsData } = useGetDelegationsQuery();
  const resolvedDelegations = delegationsData?.data?.delegations || delegations;
  const showContextSwitcher = resolvedDelegations.length > 0;

  const [contextMenuOpen, setContextMenuOpen] = React.useState(false);

  const activeContext = activeDelegationTenantId
    ? resolvedDelegations.find((d) => d.targetTenantId === activeDelegationTenantId)
    : null;

  const handleSelectContext = (tenantId) => {
    if (tenantId === null) {
      dispatch(clearDelegationContext());
    } else {
      dispatch(setDelegationContext(tenantId));
    }
    dispatch(baseApi.util.resetApiState());
    setContextMenuOpen(false);
  };

  // Detect if we're on a mobile device
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const canManageFederation = useSelector(
    selectHasPermission(PERMISSIONS.MANAGE_FEDERATION_MEMBERSHIPS)
  );

  // Check if user has leadership/admin roles
  const isLeadership = user?.roles?.some((role) =>
    [
      "President",
      "Vice President",
      "Secretary",
      "Treasurer",
      "Local Admin",
      "Trustee",
      "Steward",
      "EBoard Rep",
    ].includes(role.name),
  );
  const isAdmin = user?.roles?.some((role) => role.name === "Local Admin");

  const navigation = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: HomeIcon,
      tooltip: "Dashboard Home",
      feature: null, // Dashboard is always visible
    },
    {
      name: "News Feed",
      path: "/dashboard/feed",
      icon: NewspaperIcon,
      tooltip: "News & Updates",
      feature: "newsFeed",
    },
    {
      name: "Events",
      path: "/dashboard/events",
      icon: CalendarIcon,
      tooltip: "Union Events",
      feature: "events",
    },
    {
      name: "Grievances",
      path: "/dashboard/grievances",
      icon: ScaleIcon,
      tooltip: "My Grievances",
      feature: "grievances",
    },
    {
      name: "Find Your Rep",
      path: "/dashboard/rep-finder",
      icon: MapIcon,
      tooltip: "Find Your Representative",
      feature: "repFinder",
    },
    {
      name: "Tasks",
      path: "/dashboard/tasks",
      icon: ClipboardDocumentListIcon,
      tooltip: "My Tasks",
      feature: "tasks",
    },
    {
      name: "Documents",
      path: "/dashboard/documents",
      icon: FolderIcon,
      tooltip: "Document Library",
      feature: "documents",
    },
    {
      name: "Hall Reservations",
      path: "/dashboard/hall",
      icon: BuildingOfficeIcon,
      tooltip: "Union Hall",
      feature: "hallReservations",
    },
    {
      name: "Directory",
      path: "/dashboard/directory",
      icon: UsersIcon,
      tooltip: "Member Directory",
      feature: "memberDirectory",
    },
    {
      name: "Leadership",
      path: "/dashboard/leadership",
      icon: StarIcon,
      tooltip: "Union Leaders",
      feature: "leadership",
    },
    {
      name: "Forum",
      path: "/dashboard/forum",
      icon: ChatBubbleLeftRightIcon,
      tooltip: "Member Forum",
      feature: "forum",
    },
    {
      name: "Suggestion Box",
      path: "/dashboard/suggestion-box",
      icon: LightBulbIcon,
      tooltip: "Member Suggestions",
      feature: "suggestionBox",
    },
    {
      name: "Voting",
      path: "/dashboard/voting",
      icon: CheckCircleIcon,
      tooltip: "Polls & Voting",
      feature: "voting",
    },
    {
      name: "Store",
      path: "/dashboard/store",
      icon: ShoppingBagIcon,
      tooltip: "Union Store",
      feature: "store",
    },
    {
      name: "Peer Support",
      path: "/dashboard/peer-support",
      icon: HandRaisedIcon,
      tooltip: "Peer Support",
      feature: "peerSupport",
    },
    {
      name: "Contracts",
      path: "/dashboard/contracts",
      icon: DocumentCheckIcon,
      tooltip: "Collective Bargaining Agreements",
      feature: "contracts",
    },
    {
      name: "Resources",
      path: "/dashboard/resources",
      icon: HeartIcon,
      tooltip: "Member Resources",
      feature: null, // Resources is always visible
    },
    {
      name: "Officials",
      path: "/dashboard/officials",
      icon: UserGroupIcon,
      tooltip: "Political Officials",
      feature: "lobbying",
    },
    {
      name: "Profile",
      path: "/dashboard/profile",
      icon: UserIcon,
      tooltip: "My Profile",
      feature: null, // Profile is always visible
    },
  ];

  const leadershipNavigation = [
    {
      name: "Analytics",
      path: "/dashboard/analytics",
      icon: ChartBarIcon,
      tooltip: "Reports & Analytics",
      requiresLeadership: true,
    },
    {
      name: "E-Board Updates",
      path: "/dashboard/eboard-updates",
      icon: PresentationChartLineIcon,
      tooltip: "Monthly E-Board Updates",
      requiresLeadership: true,
      feature: "eboardUpdates",
    },
    {
      name: "Send Message",
      path: "/dashboard/communicate",
      icon: MegaphoneIcon,
      tooltip: "Send Communication",
      requiresLeadership: true,
    },
    {
      name: "Admin",
      path: "/dashboard/admin",
      icon: Cog6ToothIcon,
      tooltip: "Admin Panel",
      requiresLeadership: true, // Show to all leadership, individual features are permission-gated
    },
    {
      name: "Federation",
      path: "/dashboard/federation-admin",
      icon: GlobeAltIcon,
      tooltip: "Federation Admin",
      requiresFederation: true,
    },
  ];

  const renderNavItem = (item) => {
    // Role-based filtering
    if (item.requiresAdmin && !isAdmin) return null;
    if (item.requiresLeadership && !isLeadership && !isAdmin) return null;
    if (item.requiresFederation && !canManageFederation) return null;

    // Feature toggle filtering
    if (item.feature) {
      const featureState = features[item.feature];

      // If feature doesn't exist yet or is loading, hide it
      if (!featureState) return null;

      // Hide if feature is "off" for everyone
      if (featureState.isOff) return null;

      // Hide if feature is "disabled" and user can't view disabled features
      if (featureState.isDisabled && !featureState.canAccess) return null;
    }

    // Check if this item should be grayed out (disabled for members, visible to leadership)
    const isDisabledFeature =
      item.feature && features[item.feature]?.isDisabled;
    const showDisabledStyle = isDisabledFeature && canViewDisabled;

    const navContent = (
      <NavLink
        to={item.path}
        end={item.path === "/dashboard"}
        onClick={onMobileClose}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors relative ${
            showDisabledStyle
              ? isActive
                ? "bg-warning-700 bg-opacity-30 text-warning-200"
                : "text-warning-300 hover:bg-warning-700 hover:bg-opacity-20 opacity-60"
              : isActive
                ? "bg-primary-700 text-white"
                : "text-neutral-100 hover:bg-primary-700 hover:bg-opacity-50"
          }`
        }
      >
        {({ isActive }) => (
          <>
            {/* Active indicator */}
            {isActive && (
              <div
                className={`absolute left-0 top-0 bottom-0 w-1 rounded-r ${
                  showDisabledStyle ? "bg-warning-400" : "bg-accent-600"
                }`}
              />
            )}

            {/* Icon */}
            <item.icon className="w-6 h-6 flex-shrink-0" />

            {/* Label (shown when expanded or on mobile) */}
            {(expanded || isMobile) && (
              <span className="font-medium text-sm">{item.name}</span>
            )}

            {/* Disabled badge (shown when expanded or on mobile for disabled features) */}
            {(expanded || isMobile) && showDisabledStyle && (
              <span className="ml-auto px-2 py-0.5 text-xs font-semibold bg-warning-600 text-white rounded">
                Disabled
              </span>
            )}

            {/* Regular badge (shown when expanded or on mobile) */}
            {(expanded || isMobile) && item.badge && !showDisabledStyle && (
              <span className="ml-auto px-2 py-0.5 text-xs font-semibold bg-accent-600 text-white rounded">
                {item.badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    );

    // Wrap with tooltip if collapsed and NOT on mobile
    if (!expanded && !isMobile) {
      const tooltipContent = showDisabledStyle
        ? `${item.tooltip} (Disabled for members)`
        : item.tooltip;

      return (
        <Tooltip key={item.path} content={tooltipContent} position="right">
          {navContent}
        </Tooltip>
      );
    }

    return <div key={item.path}>{navContent}</div>;
  };

  const sidebarClasses = `
    fixed top-0 left-0 h-full bg-primary-800 text-white z-40
    transition-all duration-300 ease-in-out
    ${isMobile ? "w-60" : expanded ? "w-60" : "w-16"}
    ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
  `;

  return (
    <aside className={sidebarClasses}>
      <div className="flex flex-col h-full">
        {/* Logo/Header */}
        <div className="flex items-center justify-between p-4 border-b border-primary-700">
          {expanded ? (
            <h1 className="text-xl font-bold">Battalion Hub</h1>
          ) : (
            <div className="w-8 h-8 bg-accent-600 rounded flex items-center justify-center font-bold">
              BH
            </div>
          )}
        </div>

        {/* Context Switcher — only shown to delegated users */}
        {showContextSwitcher && (
          <div className="border-b border-primary-700">
            {expanded || isMobile ? (
              <div className="relative px-3 py-2">
                {/* Active context indicator strip */}
                {activeDelegationTenantId && (
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-accent-500" />
                )}
                <button
                  onClick={() => setContextMenuOpen((v) => !v)}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-primary-700 transition-colors text-left"
                  aria-label="Switch context"
                >
                  <GlobeAltIcon className="w-5 h-5 flex-shrink-0 text-primary-300" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-primary-300 font-medium leading-none mb-0.5">
                      Active Context
                    </div>
                    <div className="text-sm font-semibold text-white truncate">
                      {activeContext
                        ? activeContext.positionLabel || activeContext.targetTenantName
                        : `Home · ${user?.tenantName || "My Local"}`}
                    </div>
                  </div>
                  <ChevronDownIcon
                    className={`w-4 h-4 flex-shrink-0 text-primary-300 transition-transform ${
                      contextMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Dropdown */}
                {contextMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setContextMenuOpen(false)}
                    />
                    <div className="absolute left-3 right-3 top-full mt-1 bg-primary-900 border border-primary-600 rounded-lg shadow-lg z-20 overflow-hidden">
                      {/* Home option */}
                      <button
                        onClick={() => handleSelectContext(null)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-primary-700 transition-colors ${
                          !activeDelegationTenantId ? "bg-primary-700" : ""
                        }`}
                      >
                        <HomeIcon className="w-4 h-4 flex-shrink-0 text-primary-300" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white truncate">
                            Home · {user?.tenantName || "My Local"}
                          </div>
                        </div>
                        {!activeDelegationTenantId && (
                          <div className="w-2 h-2 rounded-full bg-accent-400 flex-shrink-0" />
                        )}
                      </button>

                      {/* Delegation options */}
                      {resolvedDelegations
                        .map((d) => (
                          <button
                            key={d.targetTenantId}
                            onClick={() => handleSelectContext(d.targetTenantId)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-primary-700 transition-colors border-t border-primary-700 ${
                              activeDelegationTenantId === d.targetTenantId
                                ? "bg-primary-700"
                                : ""
                            }`}
                          >
                            <GlobeAltIcon className="w-4 h-4 flex-shrink-0 text-primary-300" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-white truncate">
                                {d.positionLabel || d.targetTenantName}
                              </div>
                              {d.positionLabel && (
                                <div className="text-xs text-primary-400 truncate">
                                  {d.targetTenantName}
                                </div>
                              )}
                            </div>
                            {activeDelegationTenantId === d.targetTenantId && (
                              <div className="w-2 h-2 rounded-full bg-accent-400 flex-shrink-0" />
                            )}
                          </button>
                        ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              // Collapsed sidebar — show icon only with tooltip
              <Tooltip
                content={
                  activeContext
                    ? `Context: ${activeContext.positionLabel || activeContext.targetTenantName}`
                    : "Home Context"
                }
                position="right"
              >
                <button
                  onClick={() => setContextMenuOpen((v) => !v)}
                  className={`flex items-center justify-center w-full p-3 hover:bg-primary-700 transition-colors relative ${
                    activeDelegationTenantId ? "text-accent-400" : "text-primary-300"
                  }`}
                >
                  {activeDelegationTenantId && (
                    <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-accent-400" />
                  )}
                  <GlobeAltIcon className="w-6 h-6" />
                </button>
              </Tooltip>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-1">
          {featuresLoading ? (
            // Loading skeletons
            <>
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg animate-pulse"
                >
                  <div className="w-6 h-6 bg-primary-700 rounded flex-shrink-0" />
                  {expanded && (
                    <div className="h-4 bg-primary-700 rounded flex-1" />
                  )}
                </div>
              ))}
            </>
          ) : (
            <>
              {/* Main Navigation */}
              {navigation.map(renderNavItem)}

              {/* Divider for leadership section */}
              {(isLeadership || isAdmin) && (
                <div className="my-4 border-t border-primary-700" />
              )}

              {/* Leadership Navigation */}
              {leadershipNavigation.map(renderNavItem)}
            </>
          )}
        </nav>

        {/* Toggle Button */}
        <button
          onClick={onToggle}
          className="hidden md:flex items-center justify-center p-4 border-t border-primary-700 hover:bg-primary-700 transition-colors"
          aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
        >
          {expanded ? (
            <ChevronLeftIcon className="w-5 h-5" />
          ) : (
            <ChevronRightIcon className="w-5 h-5" />
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
